import { useEffect, useState } from 'react';

type RarcDescriptions = Readonly<Record<string, string>>;

let pending: Promise<RarcDescriptions> | null = null;
// The resolved list, kept so a component mounting after the chunk has arrived
// can render the text on its first render instead of one bare-code render later.
let loaded: RarcDescriptions | undefined;

/**
 * Load the X12 RARC text (~1,200 codes) on first use.
 *
 * The JSON is deliberately only reachable through this dynamic `import()`, so
 * the bundler emits it as its own chunk that the remittance detail dialog
 * fetches on demand instead of every user downloading it with the main bundle.
 * Nothing eagerly imported may import `rarcDescriptions.json` statically.
 *
 * The promise is cached so the chunk is requested once; a failed load is
 * forgotten so the next dialog open can try again.
 */
export function loadRarcDescriptions(): Promise<RarcDescriptions> {
  pending ??= import('./rarcDescriptions.json')
    .then(module => {
      loaded = module.default as RarcDescriptions;
      return loaded;
    })
    .catch(error => {
      pending = null;
      throw error;
    });
  return pending;
}

/**
 * The RARC text once its chunk has arrived (immediately, if an earlier dialog
 * already loaded it), `undefined` until then and if the load fails. Callers render the bare code in that case (exactly as for an
 * unknown code), so a slow or failed chunk never blocks or breaks the dialog.
 */
export function useRemarkCodeDescriptions(): RarcDescriptions | undefined {
  const [descriptions, setDescriptions] = useState(loaded);

  useEffect(() => {
    if (descriptions) return;
    let active = true;
    loadRarcDescriptions().then(
      result => {
        if (active) setDescriptions(result);
      },
      () => {
        // Bare codes are the documented fallback; nothing to surface.
      }
    );
    return () => {
      active = false;
    };
  }, [descriptions]);

  return descriptions;
}
